import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import * as orgRoutes from 'src/app/account/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useIsGraphIdAvailable } from 'src/hooks/useIsGraphIdAvailable';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { generateUniqueString } from 'src/lib/generateUniqueString';
import {
  GRAPH_ID_MAX_CHAR,
  GRAPH_NAME_MAX_CHAR,
} from 'src/lib/graphOnboarding/constants';
import { generateGraphId } from 'src/lib/graphOnboarding/helpers/utils';

import { InputCharCount } from '../../../../../components/InputCharCount';
import { FlowTypeContext } from '../FlowTypeProvider';
import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStep,
  SupergraphOnboardingStepPrivate,
} from '../shared';

import { DedicatedVariantContent } from './DedicatedVariantContent';

export const SupergraphStep = ({
  setPreviousStep,
  previousStep,
  values,
  ...contentProps
}: React.ComponentProps<typeof SupergraphStepContent> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  >) => {
  const { flowType } = useContext(FlowTypeContext);

  const [accountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !accountId,
    accountId: accountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });

  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={
        flowType === 'DEDICATED_PRIVATE'
          ? SupergraphOnboardingStepPrivate.YourGraphQLAPI
          : SupergraphOnboardingStep.Supergraph
      }
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      className={classnames(isDedicatedPlan && 'w-120')}
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <SupergraphStepContent values={values} {...contentProps} />
    </NewSupergraphOnboardingStepLayout>
  );
};

const SupergraphStepContent = ({
  values,
  errors,
  touched,
  setFieldValue,
  validateField,
  handleBlur,
  orgId,
  nextButtonState,
  setNextButtonState,
  setValues,
  creatingSupergraph,
  createSupergraph,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  setWhenStepsBegan,
  createGraphError,
  createGraphValidationError,
}: CommonStepProps &
  CommonCloudDedicatedProps & {
    orgId: string;
    creatingSupergraph: boolean;
    nextButtonState: ButtonState;
    setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
  }) => {
  const { includeVariantOnboardingStep } = useRouteParams(
    orgRoutes.supergraphOnboarding,
  );
  const isVariantOnboardingFlow = includeVariantOnboardingStep === 'true';
  useEffect(() => {
    setWhenStepsBegan((prev) => ({
      ...(prev ?? {}),
      [SupergraphOnboardingStep.Supergraph]: Date.now(),
    }));
  }, [setWhenStepsBegan]);

  const [idFieldFocused, setIdFieldFocused] = useState(false);

  const { isGraphIdAvailable, loading: graphIdAvailableDataLoading } =
    useIsGraphIdAvailable({
      accountId: orgId,
      graphId: values.supergraphID,
      skip: !!errors.supergraphID, // don't query until there is a valid id to check
    });

  const graphIdAvailable =
    // if we haven't gotten data back yet, don't say id is not available yet
    graphIdAvailableDataLoading || isGraphIdAvailable;

  // we always programmatically fill these fields,
  // so we don't want to check if they have been touched before disabling
  const disabledReason = useMemo(
    () =>
      errors.supergraphID ||
      errors.supergraphName ||
      (!isVariantOnboardingFlow && !graphIdAvailable
        ? 'This graph id is unavailable. Edit your graph id to continue.'
        : undefined),
    [
      errors.supergraphID,
      errors.supergraphName,
      graphIdAvailable,
      isVariantOnboardingFlow,
    ],
  );

  useEffect(() => {
    setNextButtonState({
      isDisabled:
        !!disabledReason ||
        values.supergraphID.length < 2 ||
        values.supergraphName.length < 2,
      disabledReason,
    });
  }, [
    disabledReason,
    setNextButtonState,
    values.supergraphID.length,
    values.supergraphName.length,
  ]);

  // Set supergraphID if not currently set & untouched
  useEffect(() => {
    if (
      !values.supergraphID &&
      !touched.supergraphID &&
      values.supergraphName
    ) {
      setFieldValue(
        'supergraphID',
        generateGraphId(values.supergraphName),
        true,
      );
    }
  }, [
    setFieldValue,
    touched.supergraphID,
    values.supergraphID,
    values.supergraphName,
  ]);

  // validate id when we fill it programmatically
  useEffect(() => {
    if (!touched.supergraphID && values.supergraphID.length > 0) {
      validateField('supergraphID');
    }
  }, [touched.supergraphID, values.supergraphID, validateField]);

  // validate id since we fill it programmatically as an initial value
  useEffect(() => {
    if (!touched.supergraphName && values.supergraphName.length > 0) {
      validateField('supergraphName');
    }
  }, [touched.supergraphName, validateField, values.supergraphName.length]);

  const headerText = isVariantOnboardingFlow
    ? 'Your Dedicated GraphOS variant details'
    : "Let's create your GraphOS Router!";

  return (
    <div>
      {!!(createGraphError?.message || createGraphValidationError) && (
        <ErrorMessage className="mb-2">
          {
            // only show the user-actionable validation errors, otherwise show default error message
          }
          {createGraphValidationError}
        </ErrorMessage>
      )}
      <div className="mb-6 text-2xl font-semibold">{headerText}</div>
      {isVariantOnboardingFlow ? (
        <DedicatedVariantContent values={values} />
      ) : (
        <>
          <div className="block text-base text-secondary lg:hidden">
            Your supergraph enables clients to fetch data from multiple
            subgraphs with a single query. Over time, you can grow your
            supergraph to include more and more of your organization’s data.
          </div>
          <FormControl
            className="mt-8"
            isInvalid={!!errors.supergraphName}
            isRequired
          >
            <FormLabel>Supergraph Name</FormLabel>
            <Input
              onBlur={handleBlur}
              onChange={(e) => {
                setValues(
                  // We want to keep the graph id in sync with graph title if the user hasn't edited yet.
                  (fields) => ({
                    ...fields,
                    supergraphName: e.target.value,
                    supergraphID: !touched.supergraphID
                      ? generateGraphId(e.target.value, generateUniqueString())
                      : values.supergraphID,
                  }),
                  // validate on change only when there are errors
                  'supergraphName' in errors,
                );
              }}
              value={values.supergraphName}
              name="supergraphName"
              placeholder="Name your supergraph"
              rightElement={
                <InputCharCount
                  count={values.supergraphName.length}
                  maxCount={GRAPH_NAME_MAX_CHAR}
                />
              }
            />
            <FormErrorMessage>{errors.supergraphName}</FormErrorMessage>
          </FormControl>
          <FormControl
            className="mt-6"
            isInvalid={!!errors.supergraphID || !graphIdAvailable}
            isRequired
          >
            <FormLabel>Supergraph ID</FormLabel>
            <Input
              onBlur={handleBlur}
              onFocus={() => setIdFieldFocused(true)}
              onChange={(e) => {
                setFieldValue('supergraphID', e.target.value, true);
              }}
              value={values.supergraphID}
              name="supergraphID"
              placeholder="Unique identifier for your supergraph"
              rightElement={
                <InputCharCount
                  count={values.supergraphID.length}
                  maxCount={GRAPH_ID_MAX_CHAR}
                />
              }
            />
            {errors.supergraphID || !graphIdAvailable ? (
              <FormErrorMessage>
                {errors.supergraphID || (
                  <>This ID is not available. Please try another one.</>
                )}
              </FormErrorMessage>
            ) : (
              graphIdAvailable && (
                <FormHelperText iconType="success">
                  Supergraph ID available
                </FormHelperText>
              )
            )}
          </FormControl>
          <div className="mt-2 text-sm text-black-lighter">
            {idFieldFocused && (
              <span>
                Choose an ID that reflects the entirety of data your supergraph
                will provide — this will be used to reference your supergraph.{' '}
              </span>
            )}
            Your ID is immutable and must be unique across all of Apollo. Make
            sure it starts with a letter and contains only letters, numbers, and
            dashes.
          </div>
          <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
            <NewSupergraphOnboardingControls.CancelButton
              creatingSupergraph={creatingSupergraph}
            />

            <NewSupergraphOnboardingControls.BackButton
              creatingSupergraph={creatingSupergraph}
              className="ml-auto"
            />
            <NewSupergraphOnboardingControls.CreateGraphButton
              whenStepsBegan={whenStepsBegan}
              smallScreen={false}
              creatingSupergraph={creatingSupergraph}
              state={nextButtonState}
              continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
              onClick={createSupergraph}
            />
          </div>
        </>
      )}
    </div>
  );
};
